import React from 'react';
import { ComputedAllergenSummary, AllergenInfo } from '../types';
import './AllergenDisplay.css';

interface AllergenDisplayProps {
    allergens: ComputedAllergenSummary;
    compact?: boolean;
    showBreakdown?: boolean;
}

export const AllergenDisplay: React.FC<AllergenDisplayProps> = ({
    allergens,
    compact = false,
    showBreakdown = false
}) => {
    if (allergens.allAllergens.length === 0) {
        return null;
    }

    const getSeverityIcon = (severity: AllergenInfo['severity']) => {
        switch (severity) {
            case 'contains': return '⚠️';
            case 'may_contain': return '⚠️';
            case 'traces': return 'ℹ️';
        }
    };

    const getSeverityColor = (severity: AllergenInfo['severity']) => {
        switch (severity) {
            case 'contains': return '#e74c3c';
            case 'may_contain': return '#f39c12';
            case 'traces': return '#3498db';
        }
    };

    const getSeverityText = (severity: AllergenInfo['severity']) => {
        switch (severity) {
            case 'contains': return 'Contains';
            case 'may_contain': return 'May contain';
            case 'traces': return 'May contain traces of';
        }
    };

    const getAllergenDisplayName = (allergen: string): string => {
        const displayNames: Record<string, string> = {
            celery: 'Celery',
            gluten: 'Gluten',
            crustaceans: 'Crustaceans',
            eggs: 'Eggs',
            fish: 'Fish',
            lupin: 'Lupin',
            milk: 'Milk',
            molluscs: 'Molluscs',
            mustard: 'Mustard',
            nuts: 'Tree Nuts',
            peanuts: 'Peanuts',
            sesame: 'Sesame',
            soya: 'Soya',
            sulphites: 'Sulphites'
        };

        return displayNames[allergen] || allergen.charAt(0).toUpperCase() + allergen.slice(1);
    };

    if (compact) {
        // Group allergens by severity for compact view
        const containsAllergens = allergens.allAllergens.filter(a => a.severity === 'contains');
        const mayContainAllergens = allergens.allAllergens.filter(a => a.severity === 'may_contain');
        const tracesAllergens = allergens.allAllergens.filter(a => a.severity === 'traces');

        // Build inline allergen text
        const allergenParts: string[] = [];
        if (containsAllergens.length > 0) {
            allergenParts.push(`Contains ${containsAllergens.map(a => getAllergenDisplayName(a.allergen)).join(', ')}`);
        }
        if (mayContainAllergens.length > 0) {
            allergenParts.push(`May contain ${mayContainAllergens.map(a => getAllergenDisplayName(a.allergen)).join(', ')}`);
        }
        if (tracesAllergens.length > 0) {
            allergenParts.push(`Traces of ${tracesAllergens.map(a => getAllergenDisplayName(a.allergen)).join(', ')}`);
        }

        return (
            <div className="allergen-display compact">
                <div className="allergen-compact-wrapper">
                    <div className="allergen-compact-inline">
                        <span className="allergen-warning-icon">⚠️</span>
                        <span className="allergen-compact-title">Allergen Info:</span>
                        <span className="allergen-inline-text">{allergenParts.join(' • ')}</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="allergen-display">
            <div className="allergen-header">
                <h4 className="allergen-title">
                    <span className="allergen-title-icon">⚠️</span>
                    Allergen Information
                </h4>
            </div>

            <div className="allergen-list">
                {allergens.allAllergens.map((allergen, idx) => (
                    <div
                        key={idx}
                        className={`allergen-item severity-${allergen.severity}`}
                        style={{ borderLeftColor: getSeverityColor(allergen.severity) }}
                    >
                        <div className="allergen-main">
                            <span className="allergen-icon">{getSeverityIcon(allergen.severity)}</span>
                            <div className="allergen-content">
                                <span className="allergen-name">{getAllergenDisplayName(allergen.allergen)}</span>
                                <span
                                    className="allergen-severity"
                                    style={{ color: getSeverityColor(allergen.severity) }}
                                >
                                    {getSeverityText(allergen.severity)}
                                </span>
                            </div>
                        </div>
                        {allergen.notes && (
                            <div className="allergen-notes">{allergen.notes}</div>
                        )}
                    </div>
                ))}
            </div>

            {showBreakdown && (
                <div className="allergen-breakdown">
                    {allergens.directAllergens.length > 0 && (
                        <div className="breakdown-section">
                            <h5>From Recipe/Preparation:</h5>
                            <ul>
                                {allergens.directAllergens.map((a, idx) => (
                                    <li key={idx}>{getAllergenDisplayName(a.allergen)}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {allergens.stockAllergens.length > 0 && (
                        <div className="breakdown-section">
                            <h5>From Ingredients:</h5>
                            <ul>
                                {allergens.stockAllergens.map((a, idx) => (
                                    <li key={idx}>{getAllergenDisplayName(a.allergen)}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {allergens.optionAllergens.size > 0 && (
                        <div className="breakdown-section">
                            <h5>From Your Selections:</h5>
                            <ul>
                                {Array.from(allergens.optionAllergens.values()).flat().map((a, idx) => (
                                    <li key={idx}>{getAllergenDisplayName(a.allergen)}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
